// BaseExamPortalPage.tsx

import { useState, useMemo, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Tabs, Tab, Stack, Grid } from "@mui/material";
import { FolderOpen } from "@mui/icons-material";
import type { QuizBackground } from "@/types/Quiz.types";
import type { BrandResolver, PortalThemeConfig } from "@/types/Portal.types";
import { ProfessionalBrandResolver } from "@/utils/brandResolvers";
import { ExamCard } from "@/components/ExamCard.tsx";

export interface BaseExamPortalPageProps {
  title: string;
  subtitle?: string;
  data?: QuizBackground[];
  fetchData?: () => Promise<QuizBackground[]>;
  brandResolver?: BrandResolver;
  themeConfig?: PortalThemeConfig;
  allCategoryLabel?: string;
  customCategories?: string[];
  onItemSelect: (item: QuizBackground) => void;
  actionText?: string | ((item: QuizBackground) => string);
  emptyStateText?: string;
  headerExtra?: ReactNode;
  isDisabled?: boolean;
}

export default function BaseExamPortalPage({
  title,
  subtitle,
  data,
  fetchData,
  brandResolver = ProfessionalBrandResolver,
  themeConfig,
  allCategoryLabel,
  customCategories,
  onItemSelect,
  actionText,
  emptyStateText,
  headerExtra,
  isDisabled = false,
}: BaseExamPortalPageProps) {
  const { t } = useTranslation();

  const resolvedAllCategoryLabel = allCategoryLabel ?? t("pages.baseExamPortal.allCategories", "All Quizzes");
  const resolvedEmptyStateText = emptyStateText ?? t("pages.baseExamPortal.emptyState", "No matching Quizzes found.");

  const [quizzes, setQuizzes] = useState<QuizBackground[]>(data || []);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(!data && !!fetchData);

  useEffect(() => {
    if (data) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQuizzes(data);
    } else if (fetchData) {
      setLoading(true);
      fetchData()
        .then((res) => setQuizzes(res))
        .finally(() => setLoading(false));
    }
  }, [data, fetchData]);

  const categories = useMemo(() => {
    if (customCategories) return customCategories;
    const uniqueLabels = new Set<string>();
    quizzes.forEach((item) => {
      item.tags?.forEach((tag) => uniqueLabels.add(tag.label));
    });
    return [resolvedAllCategoryLabel, ...Array.from(uniqueLabels)];
  }, [quizzes, customCategories, resolvedAllCategoryLabel]);

  const filteredItems = useMemo(() => {
    const currentCategory = categories[activeTab];
    if (!currentCategory || currentCategory === resolvedAllCategoryLabel) return quizzes;
    return quizzes.filter((item) => item.tags?.some((tag) => tag.label === currentCategory));
  }, [activeTab, categories, quizzes, resolvedAllCategoryLabel]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Header Block */}
        <Stack
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            mb: 5,
            justifyContent: "space-between",
            alignItems: { sm: "center" },
          }}
        >
          <Box>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 800,
                color: themeConfig?.textColorPrimary || "#0f172a",
                mb: 0.5,
                letterSpacing: "-0.01em",
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: themeConfig?.textColorSecondary || "#64748b" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {headerExtra && <Box sx={{ mt: { xs: 2, sm: 0 } }}>{headerExtra}</Box>}
        </Stack>

        {/* Minimal Pill Filter */}
        {categories.length > 1 && (
          <Box sx={{ mb: 5 }}>
            <Tabs
              value={activeTab}
              onChange={(_, val) => setActiveTab(val)}
              variant="scrollable"
              slotProps={{
                indicator: { style: { display: "none" } },
              }}
              sx={{ minHeight: "auto", "& .MuiTabs-flexContainer": { gap: 1 } }}
            >
              {categories.map((cat, idx) => (
                <Tab
                  key={idx}
                  label={cat}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.80rem",
                    minHeight: "auto",
                    py: 0.8,
                    px: 2,
                    borderRadius: 2,
                    color: themeConfig?.textColorSecondary || "#64748b",
                    bgcolor: activeTab === idx ? themeConfig?.activeTabBg || "#0f172a" : "#ffffff",
                    border: "1px solid",
                    borderColor:
                      activeTab === idx ? themeConfig?.activeTabBg || "#0f172a" : themeConfig?.borderColor || "#e2e8f0",
                    transition: "all 0.2s ease",
                    "&.Mui-selected": { color: themeConfig?.activeTabTextColor || "#ffffff" },
                  }}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Grid Cards */}
        <Grid container spacing={3.5}>
          {filteredItems.map((item) => {
            const brand = brandResolver(item);
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.quizId}>
                <ExamCard
                  item={item}
                  brand={brand}
                  themeConfig={themeConfig}
                  actionText={actionText}
                  onActionClick={onItemSelect}
                  isDisabled={isDisabled}
                />
              </Grid>
            );
          })}

          {/* Empty State */}
          {!loading && filteredItems.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Stack
                sx={{
                  py: 8,
                  textAlign: "center",
                  bgcolor: "#ffffff",
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: themeConfig?.borderColor || "#e2e8f0",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FolderOpen sx={{ fontSize: 36, color: "#cbd5e1", mb: 1.5 }} />
                <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                  {resolvedEmptyStateText}
                </Typography>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}
